import { useMemo, useState } from "react";
import { ArrowUpDown, RefreshCw } from "lucide-react";
import useWorkspaceOverview from "./useWorkspaceOverview";
import StatusBadge from "../components/StatusBadge";

const SECTION_CONFIG = {
  enterprises: {
    columns: [
      ["organizationName", "Enterprise"],
      ["ownerName", "Owner"],
      ["ownerEmail", "Email"],
      ["packageName", "Package"],
      ["daysLeft", "Days Left"],
      ["renewal", "Renewal"],
      ["cardUsage", "Card Usage"],
      ["health", "Health"],
    ],
  },
  customers: {
    columns: [
      ["name", "Customer"],
      ["email", "Email"],
      ["organizationName", "Organization"],
      ["packageName", "Package"],
      ["daysLeft", "Days Left"],
      ["renewal", "Renewal"],
      ["cardUsage", "Card Usage"],
      ["reportCount", "Reports"],
      ["health", "Health"],
    ],
  },
  vouchers: {
    columns: [
      ["name", "Voucher"],
      ["code", "Code"],
      ["percent", "Discount"],
      ["limitUsage", "Usage"],
      ["daysLeft", "Days Left"],
      ["startsAt", "Starts"],
      ["endsAt", "Ends"],
      ["status", "Status"],
    ],
  },
  plans: {
    columns: [
      ["name", "Plan"],
      ["days", "Days"],
      ["cards", "Cards"],
      ["designs", "Designs"],
      ["admins", "Admins"],
      ["services", "Services"],
      ["amount", "Amount"],
      ["status", "Status"],
    ],
  },
  subscriptions: {
    columns: [
      ["organizationName", "Organization"],
      ["packageName", "Package"],
      ["packageDays", "Duration"],
      ["daysLeft", "Days Left"],
      ["renewal", "Renewal"],
      ["cardUsage", "Card Usage"],
      ["health", "Health"],
    ],
  },
  reports: {
    columns: [
      ["organizationName", "Organization"],
      ["reportCount", "Reports"],
      ["packageName", "Package"],
      ["daysLeft", "Days Left"],
      ["renewal", "Renewal"],
      ["cardUsage", "Card Usage"],
      ["health", "Health"],
    ],
  },
  admins: {
    columns: [
      ["name", "Admin"],
      ["email", "Email"],
      ["role", "Role"],
      ["organizationName", "Organization"],
      ["packageName", "Package"],
      ["daysLeft", "Days Left"],
      ["cardUsage", "Card Usage"],
      ["webhookAccess", "Webhook"],
      ["health", "Health"],
    ],
  },
  auditLogs: {
    columns: [
      ["title", "Event"],
      ["organizationName", "Organization"],
      ["detail", "Detail"],
      ["severity", "Severity"],
      ["createdAt", "Created"],
    ],
  },
};

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

function formatDays(value) {
  if (value == null) return "-";
  if (value < 0) return `${Math.abs(value)} days overdue`;
  if (value === 0) return "Today";
  return `${value} days`;
}

function renderCardUsage(row) {
  const total = Number(row.totalCards || 0);
  const used = Number(row.usedCards || 0);
  const remaining = Number(row.remainingCards || 0);
  const utilization = total > 0 ? Number(row.utilization || ((used / total) * 100).toFixed(1)) : 0;

  return (
    <div className="tw-min-w-[180px] tw-space-y-2">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-xs">
        <span className="admin-v2-text-muted">{used} used</span>
        <span className="admin-v2-text-strong">{remaining} left</span>
      </div>
      <div className="admin-v2-progress">
        <span style={{ width: `${Math.min(utilization, 100)}%` }} />
      </div>
      <p className="tw-text-xs admin-v2-text-muted">{total} total cards</p>
    </div>
  );
}

function renderLimitUsage(row) {
  const used = Number(row.used || 0);
  const limit = Number(row.limit || 0);
  const remaining = Number(row.remaining || 0);
  const percent = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;

  return (
    <div className="tw-min-w-[180px] tw-space-y-2">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-xs">
        <span className="admin-v2-text-muted">{used} redeemed</span>
        <span className="admin-v2-text-strong">{remaining} left</span>
      </div>
      <div className="admin-v2-progress">
        <span style={{ width: `${percent}%` }} />
      </div>
      <p className="tw-text-xs admin-v2-text-muted">{limit} total limit</p>
    </div>
  );
}

function formatValue(key, value, row) {
  if (key === "health" || key === "status" || key === "severity") {
    return <StatusBadge status={value} />;
  }

  if (key === "renewal") {
    return formatDate(row.nextPaymentDate);
  }

  if (key === "cardUsage") {
    return renderCardUsage(row);
  }

  if (key === "limitUsage") {
    return renderLimitUsage(row);
  }

  if (key === "webhookAccess") {
    return <StatusBadge status={value ? "Enabled" : "Disabled"} />;
  }

  if (key === "daysLeft") {
    return formatDays(value);
  }

  if (key === "percent") {
    return `${value || 0}%`;
  }

  if (key === "amount") {
    return `⃁ ${value || 0}`;
  }

  if (["startsAt", "endsAt", "createdAt"].includes(key)) {
    return formatDate(value);
  }

  if (
    [
      "usedCards",
      "remainingCards",
      "totalCards",
      "limit",
      "used",
      "remaining",
      "cards",
      "designs",
      "admins",
      "services",
      "reportCount",
      "days",
      "packageDays",
      "trialDays",
      "members",
    ].includes(key)
  ) {
    return value ?? 0;
  }

  return value || "-";
}

function buildHighlights(section, rows) {
  const withDaysLeft = rows.filter((row) => typeof row.daysLeft === "number");
  const lowCapacity = rows.filter(
    (row) => typeof row.remainingCards === "number" && row.remainingCards <= 5
  );
  const expired = withDaysLeft.filter((row) => row.daysLeft < 0);
  const expiringSoon = withDaysLeft.filter((row) => row.daysLeft >= 0 && row.daysLeft <= 14);
  const highUsage = rows.filter(
    (row) => typeof row.utilization === "number" && row.utilization >= 80
  );

  const defaultHighlights = [
    { label: "Rows", value: rows.length, hint: "Visible records in this section" },
    { label: "Expiring soon", value: expiringSoon.length, hint: "Renewal inside 14 days" },
    { label: "Expired", value: expired.length, hint: "Needs admin attention now" },
    { label: "Low capacity", value: lowCapacity.length, hint: "5 or fewer cards remaining" },
  ];

  if (section === "vouchers") {
    return [
      { label: "Rows", value: rows.length, hint: "Voucher records available" },
      {
        label: "Ending soon",
        value: rows.filter((row) => typeof row.daysLeft === "number" && row.daysLeft <= 7 && row.daysLeft >= 0).length,
        hint: "Will expire within a week",
      },
      {
        label: "Redeemed",
        value: rows.reduce((sum, row) => sum + Number(row.used || 0), 0),
        hint: "Total redemptions across vouchers",
      },
      {
        label: "Remaining",
        value: rows.reduce((sum, row) => sum + Number(row.remaining || 0), 0),
        hint: "Unused voucher capacity",
      },
    ];
  }

  if (section === "plans") {
    return [
      { label: "Rows", value: rows.length, hint: "Plan variants available" },
      {
        label: "Active plans",
        value: rows.filter((row) => String(row.status).toLowerCase() === "active").length,
        hint: "Live package options",
      },
      {
        label: "Total card pool",
        value: rows.reduce((sum, row) => sum + Number(row.cards || 0), 0),
        hint: "Combined card allowance",
      },
      {
        label: "Service slots",
        value: rows.reduce((sum, row) => sum + Number(row.services || 0), 0),
        hint: "Configured plan services",
      },
    ];
  }

  if (section === "auditLogs") {
    return [
      { label: "Rows", value: rows.length, hint: "Recent audit events" },
      {
        label: "High severity",
        value: rows.filter((row) => String(row.severity).toLowerCase() === "high").length,
        hint: "Immediate follow-up needed",
      },
      {
        label: "Medium severity",
        value: rows.filter((row) => String(row.severity).toLowerCase() === "medium").length,
        hint: "Watchlist events",
      },
      {
        label: "Low severity",
        value: rows.filter((row) => String(row.severity).toLowerCase() === "low").length,
        hint: "Healthy snapshots",
      },
    ];
  }

  if (section === "reports") {
    return [
      { label: "Rows", value: rows.length, hint: "Workspaces in the report view" },
      {
        label: "Total reports",
        value: rows.reduce((sum, row) => sum + Number(row.reportCount || 0), 0),
        hint: "Generated account reports",
      },
      {
        label: "High usage",
        value: highUsage.length,
        hint: "80% or more card pool used",
      },
      {
        label: "Expiring soon",
        value: expiringSoon.length,
        hint: "Renewals due in 14 days",
      },
    ];
  }

  return defaultHighlights;
}

export default function WorkspaceSectionPage({ section, title, description }) {
  const { data, loading, error, refresh } = useWorkspaceOverview();
  const rows = useMemo(() => data?.[section] || [], [data, section]);
  const columns = SECTION_CONFIG[section]?.columns || [];
  const highlights = buildHighlights(section, rows);
  const [sortKey, setSortKey] = useState(columns[0]?.[0] || "id");
  const [sortDirection, setSortDirection] = useState("asc");

  const sortedRows = useMemo(() => {
    const nextRows = [...rows];
    const factor = sortDirection === "asc" ? 1 : -1;

    nextRows.sort((leftRow, rightRow) => {
      const left = leftRow?.[sortKey];
      const right = rightRow?.[sortKey];

      if (typeof left === "number" || typeof right === "number") {
        return ((Number(left) || 0) - (Number(right) || 0)) * factor;
      }

      return (
        String(left || "").localeCompare(String(right || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * factor
      );
    });

    return nextRows;
  }, [rows, sortKey, sortDirection]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDirection("asc");
  };

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-5 sm:tw-p-6 lg:tw-p-8">
        <div className="tw-flex tw-flex-col tw-gap-5 lg:tw-flex-row lg:tw-items-end lg:tw-justify-between">
          <div className="tw-max-w-3xl tw-space-y-3">
            <p className="admin-v2-eyebrow">{title}</p>
            <h1 className="admin-v2-title">{title}</h1>
            <p className="admin-v2-subtitle">{description}</p>
          </div>

          <button type="button" className="admin-v2-action admin-v2-action-secondary" onClick={refresh}>
            <RefreshCw size={15} />
            Refresh data
          </button>
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-grid tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
          {highlights.map((item) => (
            <div key={item.label} className="admin-v2-stat-panel">
              <p className="admin-v2-eyebrow tw-mb-2">{item.label}</p>
              <p className="tw-text-3xl tw-font-semibold admin-v2-text-strong">{item.value}</p>
              <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">{item.hint}</p>
            </div>
          ))}
        </div>

        {/* <div className="admin-v2-brand-band tw-mt-4 tw-rounded-[22px] tw-p-4 sm:tw-p-5">
          <div className="tw-grid tw-gap-3 md:tw-grid-cols-[minmax(0,1fr)_auto] md:tw-items-center">
            <div>
              <p className="admin-v2-eyebrow tw-mb-2">Coverage</p>
              <p className="tw-text-sm tw-leading-6 admin-v2-text-muted">
                Wide desktop table plus mobile-ready record cards so package duration, renewal timing, card usage, and account health stay readable on every screen.
              </p>
            </div>
            <dl className="admin-v2-key-value">
              <dt>Generated</dt>
              <dd>{data?.generatedAt ? new Date(data.generatedAt).toLocaleString() : "-"}</dd>
            </dl>
          </div>
        </div> */}
      </section>

      <section className="admin-v2-table-wrap tw-p-3 lg:tw-p-4">
        {loading ? (
          <div className="tw-p-8 admin-v2-text-muted">Loading {title.toLowerCase()}...</div>
        ) : error ? (
          <div className="admin-v2-status-note tw-m-3 tw-p-4 tw-text-sm">{error}</div>
        ) : sortedRows.length === 0 ? (
          <div className="tw-p-8 admin-v2-text-muted">No {title.toLowerCase()} found.</div>
        ) : (
          <>
            <div className="tw-hidden lg:tw-block tw-overflow-x-auto">
              <table className="admin-v2-data-table">
                <thead>
                  <tr>
                    {columns.map(([key, label]) => (
                      <th key={key}>
                        <button type="button" className="admin-v2-sort-button" onClick={() => toggleSort(key)}>
                          {label}
                          <ArrowUpDown size={14} />
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => (
                    <tr key={row.id || row.accountId}>
                      {columns.map(([key]) => (
                        <td key={key}>{formatValue(key, row[key], row)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="admin-v2-data-grid lg:tw-hidden">
              {sortedRows.map((row) => (
                <article key={row.id || row.accountId} className="admin-v2-mobile-card">
                  <div className="tw-mb-4 tw-flex tw-items-start tw-justify-between tw-gap-3">
                    <div>
                      <p className="tw-text-base tw-font-semibold admin-v2-text-strong">
                        {row.organizationName || row.name || row.title || "-"}
                      </p>
                      <p className="tw-text-sm admin-v2-text-muted">
                        {row.packageName || row.email || row.code || row.detail || description}
                      </p>
                    </div>
                    {row.health ? <StatusBadge status={row.health} /> : row.status ? <StatusBadge status={row.status} /> : null}
                  </div>

                  <dl>
                    {columns.map(([key, label]) => (
                      <div key={key}>
                        <dt>{label}</dt>
                        <dd>{formatValue(key, row[key], row)}</dd>
                      </div>
                    ))}
                  </dl>
                </article>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
